import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface Props {
  children: ReactNode;
  /** When true, also require the user to have a seller profile; otherwise redirect to /seller/onboarding. */
  requireSellerProfile?: boolean;
}

/**
 * Wraps protected routes. Redirects to /login?next=<current> when unauthenticated.
 * If requireSellerProfile=true and the user has no seller profile, redirects to
 * /seller/onboarding so the user can create one.
 */
export default function RequireAuth({ children, requireSellerProfile = false }: Props): JSX.Element {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-stone-500 text-sm" role="status">
        Loading...
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (requireSellerProfile && !user.sellerProfileExists) {
    return <Navigate to="/seller/onboarding" replace />;
  }

  return <>{children}</>;
}
